import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, money } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft, Wrench, AlertTriangle, Plus, Trash2, Gauge, Clock, MapPin, User, Droplet,
  CheckCircle2, CalendarClock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fileUrl = (p) => `${API}/files/${p}?auth=${localStorage.getItem("nlc_token")}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : "—");
const oosText = (m) => {
  if (!m) return "0 mins";
  const d = Math.floor(m / 1440), h = Math.floor((m % 1440) / 60), min = Math.round(m % 60);
  return [d && `${d}d`, h && `${h}h`, min && `${min}m`].filter(Boolean).join(" ") || "0 mins";
};
const woColor = { open: "bg-amber-100 text-amber-700", in_progress: "bg-blue-100 text-blue-700", completed: "bg-primary/10 text-primary" };
const prColor = { low: "text-muted-foreground", medium: "text-amber-600", high: "text-destructive" };
const dueColor = { ok: "border-primary/30 text-primary", due_soon: "border-amber-400 text-amber-700", overdue: "border-destructive/40 text-destructive" };

const Section = ({ title, right, children, testid }) => (
  <Card className="p-0 overflow-hidden" data-testid={testid}>
    <div className="flex items-center justify-between bg-muted/60 px-4 py-3 border-b border-border">
      <h3 className="font-display font-bold text-sm">{title}</h3>
      {right}
    </div>
    <div className="p-4">{children}</div>
  </Card>
);

export default function EquipmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [d, setD] = useState(null);
  const [team, setTeam] = useState([]);
  const [woOpen, setWoOpen] = useState(false);
  const [wo, setWo] = useState({ title: "", description: "", priority: "medium", cost: 0, assigned_to: "", set_out_of_service: false });
  const [schedOpen, setSchedOpen] = useState(false);
  const [sched, setSched] = useState({ name: "", interval_type: "days", interval_value: 90, meter_field: "odometer", last_service_date: "", last_service_meter: 0, notes: "" });
  const [logOpen, setLogOpen] = useState(false);
  const [log, setLog] = useState({ type: "repair", title: "", cost: 0, notes: "", date: "" });
  const [fluidOpen, setFluidOpen] = useState(false);
  const [fluid, setFluid] = useState({ name: "", type: "", capacity: "", last_changed: "", cost: 0, notes: "" });
  const [confirm, setConfirm] = useState(null); // {kind, id}

  const load = () => api.get(`/equipment/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => { load(); api.get("/team").then((r) => setTeam(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, [id]);

  if (!d) return <div className="p-8 text-muted-foreground">Loading…</div>;
  const e = d.equipment;
  const oos = (e.status || "in_service") === "out_of_service";

  const toggleStatus = async () => {
    const next = oos ? "in_service" : "out_of_service";
    try { await api.put(`/equipment/${id}/status`, { status: next }); toast.success(next === "in_service" ? "Back in service" : "Marked out of service"); load(); }
    catch { toast.error("Could not update status"); }
  };

  const saveWo = async () => {
    if (!wo.title.trim()) return toast.error("Title is required");
    try { await api.post("/work-orders", { equipment_id: id, ...wo, cost: Number(wo.cost) || 0 }); toast.success("Work order created"); setWoOpen(false); setWo({ title: "", description: "", priority: "medium", cost: 0, assigned_to: "", set_out_of_service: false }); load(); }
    catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail)); }
  };
  const setWoStatus = async (w, status) => { try { await api.put(`/work-orders/${w.id}`, { status }); load(); } catch { toast.error("Failed"); } };

  const saveSched = async () => {
    if (!sched.name.trim()) return toast.error("Name is required");
    try { await api.post(`/equipment/${id}/maint-schedules`, { ...sched, interval_value: Number(sched.interval_value) || 0, last_service_meter: Number(sched.last_service_meter) || 0 }); toast.success("Maintenance schedule added"); setSchedOpen(false); load(); }
    catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail)); }
  };
  const completeSched = async (sid) => { try { await api.post(`/maint-schedules/${sid}/complete`); toast.success("Marked serviced"); load(); } catch { toast.error("Failed"); } };

  const saveLog = async () => {
    if (!log.title.trim()) return toast.error("Title is required");
    try { await api.post(`/equipment/${id}/logs`, { ...log, cost: Number(log.cost) || 0 }); toast.success("Record added"); setLogOpen(false); setLog({ type: "repair", title: "", cost: 0, notes: "", date: "" }); load(); }
    catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail)); }
  };

  const saveFluid = async () => {
    if (!fluid.name.trim()) return toast.error("Name is required");
    try { await api.post(`/equipment/${id}/fluids`, { ...fluid, cost: Number(fluid.cost) || 0 }); toast.success("Fluid added"); setFluidOpen(false); setFluid({ name: "", type: "", capacity: "", last_changed: "", cost: 0, notes: "" }); load(); }
    catch (er) { toast.error(formatApiErrorDetail(er.response?.data?.detail)); }
  };

  const doConfirm = async () => {
    const { kind, cid } = confirm;
    try {
      if (kind === "wo") await api.delete(`/work-orders/${cid}`);
      if (kind === "sched") await api.delete(`/maint-schedules/${cid}`);
      if (kind === "log") await api.delete(`/logs/${cid}`);
      if (kind === "fluid") await api.delete(`/equipment/${id}/fluids/${cid}`);
      if (kind === "equipment") { await api.delete(`/equipment/${id}`); toast.success("Equipment deleted"); return navigate("/equipment"); }
      toast.success("Deleted"); load();
    } catch { toast.error("Delete failed"); }
    finally { setConfirm(null); }
  };

  const fluidsTotal = (e.fluids || []).reduce((s, f) => s + (Number(f.cost) || 0), 0);

  return (
    <div className="space-y-5" data-testid="equipment-detail-page">
      <button onClick={() => navigate("/equipment")} data-testid="back-to-equipment" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to Equipment
      </button>

      {/* Overview */}
      <Card className="p-0 overflow-hidden">
        <div className="grid md:grid-cols-[220px_1fr] gap-0">
          <div className="h-48 md:h-full bg-muted min-h-[180px]">
            {e.photo ? <img src={fileUrl(e.photo)} alt={e.name} className="w-full h-full object-cover" data-testid="detail-equipment-photo" />
              : <div className="w-full h-full flex items-center justify-center text-muted-foreground"><Wrench className="h-10 w-10 opacity-40" /></div>}
          </div>
          <div className="p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="font-display text-2xl font-black tracking-tight">{e.name}</h1>
                <p className="text-sm text-muted-foreground mt-0.5">{[e.category, e.make, e.model, e.year].filter(Boolean).join(" · ")}</p>
              </div>
              {isAdmin && <Button variant="outline" size="sm" className="text-destructive" data-testid="delete-equipment-btn" onClick={() => setConfirm({ kind: "equipment" })}><Trash2 className="h-4 w-4 mr-1" />Delete</Button>}
            </div>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-sm">
              <div className="flex items-center gap-1.5 text-muted-foreground"><MapPin className="h-4 w-4" /> {e.location}</div>
              <div className="flex items-center gap-1.5 text-muted-foreground"><User className="h-4 w-4" /> {e.assigned_to_name || "Unassigned"}</div>
              {e.serial && <div className="text-muted-foreground">S/N: {e.serial}</div>}
              {e.track_odometer && <div className="flex items-center gap-1.5 text-muted-foreground"><Gauge className="h-4 w-4" /> {Number(e.current_odometer).toLocaleString()} mi</div>}
              {e.track_engine_hours && <div className="flex items-center gap-1.5 text-muted-foreground"><Clock className="h-4 w-4" /> {e.current_engine_hours} hrs</div>}
              {e.purchase_date && <div className="flex items-center gap-1.5 text-muted-foreground"><CalendarClock className="h-4 w-4" /> {fmtDate(e.purchase_date)}</div>}
            </div>
            {e.notes && <p className="mt-3 text-sm text-muted-foreground">{e.notes}</p>}

            <div className="mt-4 flex flex-wrap items-center gap-4 rounded-lg border border-border p-3">
              <div className="flex items-center gap-2">
                <Switch checked={!oos} onCheckedChange={isAdmin ? toggleStatus : undefined} disabled={!isAdmin} data-testid="in-service-toggle" />
                <span className="text-sm font-semibold">{oos ? "Out of service" : "In service"}</span>
              </div>
              <div className="text-sm text-muted-foreground">Total time OOS: <span className="font-semibold text-foreground" data-testid="oos-total">{oosText(e.oos_minutes)}</span></div>
            </div>
          </div>
        </div>
      </Card>

      {/* Work Orders */}
      <Section testid="work-orders-section" title={`Work Orders (${d.work_orders.length})`}
        right={<Button size="sm" variant="outline" data-testid="new-wo-btn" onClick={() => setWoOpen(true)}><Plus className="h-4 w-4 mr-1" />New Work Order</Button>}>
        {d.work_orders.length === 0 ? <p className="text-sm text-muted-foreground">No work orders. Report an issue to create one.</p> : (
          <div className="space-y-2">
            {d.work_orders.map((w) => (
              <div key={w.id} data-testid={`wo-${w.id}`} className="rounded-lg border border-border p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{w.title}</span>
                      <Badge variant="outline" className={`text-[0.65rem] ${prColor[w.priority] || ""}`}>{w.priority}</Badge>
                    </div>
                    {w.description && <p className="text-xs text-muted-foreground mt-1">{w.description}</p>}
                    <p className="text-[0.7rem] text-muted-foreground mt-1">Reported by {w.reported_by || "—"} · {fmtDate(w.created_at)}{w.cost ? ` · ${money(w.cost)}` : ""}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Badge className={`text-[0.65rem] ${woColor[w.status]}`}>{w.status.replace("_", " ")}</Badge>
                  </div>
                </div>
                {isAdmin && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {w.status !== "in_progress" && w.status !== "completed" && <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`wo-start-${w.id}`} onClick={() => setWoStatus(w, "in_progress")}>Start</Button>}
                    {w.status !== "completed" && <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`wo-complete-${w.id}`} onClick={() => setWoStatus(w, "completed")}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Complete</Button>}
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-destructive" data-testid={`wo-delete-${w.id}`} onClick={() => setConfirm({ kind: "wo", cid: w.id })}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Maintenance */}
      <Section testid="maintenance-section" title={`Maintenance (${d.schedules.length})`}
        right={isAdmin && <Button size="sm" variant="outline" data-testid="new-sched-btn" onClick={() => setSchedOpen(true)}><Plus className="h-4 w-4 mr-1" />Add Schedule</Button>}>
        {d.schedules.length === 0 ? <p className="text-sm text-muted-foreground">No maintenance schedules yet.</p> : (
          <div className="space-y-2">
            {d.schedules.map((s) => (
              <div key={s.id} data-testid={`sched-${s.id}`} className="flex items-center justify-between rounded-lg border border-border p-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{s.name}</span>
                    <Badge variant="outline" className={`text-[0.65rem] ${dueColor[s.due.status]}`}>{s.due.status === "ok" ? "On track" : s.due.status === "due_soon" ? "Due soon" : "Overdue"}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Every {s.interval_value} {s.interval_type === "days" ? "days" : s.interval_type === "hours" ? "engine hrs" : "miles"}
                    {s.due.next_due && ` · next: ${s.due.next_due}`}
                    {s.last_service_date && ` · last: ${fmtDate(s.last_service_date)}`}
                  </p>
                </div>
                {isAdmin && (
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`sched-complete-${s.id}`} onClick={() => completeSched(s.id)}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Serviced</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-destructive" data-testid={`sched-delete-${s.id}`} onClick={() => setConfirm({ kind: "sched", cid: s.id })}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Fluids */}
      <Section testid="fluids-section" title="Fluids" right={<div className="flex items-center gap-2"><span className="text-sm text-muted-foreground">Total: {money(fluidsTotal)}</span>{isAdmin && <Button size="sm" variant="outline" data-testid="new-fluid-btn" onClick={() => setFluidOpen(true)}><Plus className="h-4 w-4 mr-1" />Add</Button>}</div>}>
        {(e.fluids || []).length === 0 ? <p className="text-sm text-muted-foreground">No fluids tracked.</p> : (
          <div className="space-y-2">
            {(e.fluids || []).map((f) => (
              <div key={f.id} data-testid={`fluid-${f.id}`} className="flex items-center justify-between rounded-lg border border-border p-3">
                <div>
                  <span className="font-semibold text-sm flex items-center gap-1.5"><Droplet className="h-3.5 w-3.5 text-blue-500" />{f.name}</span>
                  <p className="text-xs text-muted-foreground mt-0.5">{[f.type, f.capacity, f.last_changed && `changed ${fmtDate(f.last_changed)}`, f.cost ? money(f.cost) : ""].filter(Boolean).join(" · ")}</p>
                </div>
                {isAdmin && <Button size="sm" variant="ghost" className="h-7 text-xs text-destructive" data-testid={`fluid-delete-${f.id}`} onClick={() => setConfirm({ kind: "fluid", cid: f.id })}><Trash2 className="h-3.5 w-3.5" /></Button>}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Service history */}
      <Section testid="history-section" title={`Service History (${d.logs.length})`}
        right={isAdmin && <Button size="sm" variant="outline" data-testid="new-log-btn" onClick={() => setLogOpen(true)}><Plus className="h-4 w-4 mr-1" />Add Record</Button>}>
        {d.logs.length === 0 ? <p className="text-sm text-muted-foreground">No service records yet.</p> : (
          <div className="space-y-2">
            {d.logs.map((l) => (
              <div key={l.id} data-testid={`log-${l.id}`} className="flex items-start justify-between rounded-lg border border-border p-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{l.title}</span>
                    <Badge variant="outline" className="text-[0.65rem] capitalize">{l.type}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{fmtDate(l.date)}{l.performed_by ? ` · ${l.performed_by}` : ""}{l.meter_reading != null ? ` · ${l.meter_reading}` : ""}{l.cost ? ` · ${money(l.cost)}` : ""}</p>
                  {l.notes && <p className="text-xs text-muted-foreground mt-1">{l.notes}</p>}
                </div>
                {isAdmin && <Button size="sm" variant="ghost" className="h-7 text-xs text-destructive" data-testid={`log-delete-${l.id}`} onClick={() => setConfirm({ kind: "log", cid: l.id })}><Trash2 className="h-3.5 w-3.5" /></Button>}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* New Work Order dialog */}
      <Dialog open={woOpen} onOpenChange={setWoOpen}>
        <DialogContent data-testid="wo-dialog">
          <DialogHeader><DialogTitle className="font-display">New Work Order — {e.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5"><Label>What's the issue? *</Label><Input data-testid="wo-title" value={wo.title} onChange={(ev) => setWo({ ...wo, title: ev.target.value })} placeholder="e.g. Blade won't engage" /></div>
            <div className="space-y-1.5"><Label>How it broke / details</Label><Textarea data-testid="wo-desc" value={wo.description} onChange={(ev) => setWo({ ...wo, description: ev.target.value })} rows={3} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Priority</Label>
                <Select value={wo.priority} onValueChange={(v) => setWo({ ...wo, priority: v })}>
                  <SelectTrigger data-testid="wo-priority"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Est. cost</Label><Input type="number" data-testid="wo-cost" value={wo.cost} onChange={(ev) => setWo({ ...wo, cost: ev.target.value })} /></div>
            </div>
            <div className="space-y-1.5">
              <Label>Assign to</Label>
              <Select value={wo.assigned_to || "none"} onValueChange={(v) => setWo({ ...wo, assigned_to: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="wo-assigned"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Unassigned</SelectItem>{team.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <Label className="cursor-pointer text-sm">Also mark this equipment out of service</Label>
              <Switch checked={wo.set_out_of_service} onCheckedChange={(v) => setWo({ ...wo, set_out_of_service: v })} data-testid="wo-oos-switch" />
            </div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setWoOpen(false)}>Cancel</Button><Button data-testid="save-wo-btn" onClick={saveWo} className="bg-primary text-primary-foreground hover:bg-[#152e17]">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Maintenance schedule dialog */}
      <Dialog open={schedOpen} onOpenChange={setSchedOpen}>
        <DialogContent data-testid="sched-dialog">
          <DialogHeader><DialogTitle className="font-display">Add Maintenance Schedule</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5"><Label>Name *</Label><Input data-testid="sched-name" value={sched.name} onChange={(ev) => setSched({ ...sched, name: ev.target.value })} placeholder="e.g. Oil change, Blade sharpen" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Interval type</Label>
                <Select value={sched.interval_type} onValueChange={(v) => setSched({ ...sched, interval_type: v })}>
                  <SelectTrigger data-testid="sched-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="days">By time (days)</SelectItem><SelectItem value="miles">By miles</SelectItem><SelectItem value="hours">By engine hours</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Every</Label><Input type="number" data-testid="sched-value" value={sched.interval_value} onChange={(ev) => setSched({ ...sched, interval_value: ev.target.value })} /></div>
            </div>
            {sched.interval_type === "days" ? (
              <div className="space-y-1.5"><Label>Last serviced date</Label><Input type="date" data-testid="sched-last-date" value={sched.last_service_date} onChange={(ev) => setSched({ ...sched, last_service_date: ev.target.value })} /></div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Meter</Label>
                  <Select value={sched.meter_field} onValueChange={(v) => setSched({ ...sched, meter_field: v })}>
                    <SelectTrigger data-testid="sched-meter"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="odometer">Odometer (mi)</SelectItem><SelectItem value="engine_hours">Engine hours</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Reading at last service</Label><Input type="number" data-testid="sched-last-meter" value={sched.last_service_meter} onChange={(ev) => setSched({ ...sched, last_service_meter: ev.target.value })} /></div>
              </div>
            )}
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setSchedOpen(false)}>Cancel</Button><Button data-testid="save-sched-btn" onClick={saveSched} className="bg-primary text-primary-foreground hover:bg-[#152e17]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add record dialog */}
      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent data-testid="log-dialog">
          <DialogHeader><DialogTitle className="font-display">Add Service Record</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select value={log.type} onValueChange={(v) => setLog({ ...log, type: v })}>
                  <SelectTrigger data-testid="log-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="repair">Repair</SelectItem><SelectItem value="maintenance">Maintenance</SelectItem><SelectItem value="fluid">Fluid change</SelectItem><SelectItem value="note">Note</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Date</Label><Input type="date" data-testid="log-date" value={log.date} onChange={(ev) => setLog({ ...log, date: ev.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>What was done *</Label><Input data-testid="log-title" value={log.title} onChange={(ev) => setLog({ ...log, title: ev.target.value })} /></div>
            <div className="space-y-1.5"><Label>Cost</Label><Input type="number" data-testid="log-cost" value={log.cost} onChange={(ev) => setLog({ ...log, cost: ev.target.value })} /></div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea data-testid="log-notes" value={log.notes} onChange={(ev) => setLog({ ...log, notes: ev.target.value })} rows={2} /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setLogOpen(false)}>Cancel</Button><Button data-testid="save-log-btn" onClick={saveLog} className="bg-primary text-primary-foreground hover:bg-[#152e17]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add fluid dialog */}
      <Dialog open={fluidOpen} onOpenChange={setFluidOpen}>
        <DialogContent data-testid="fluid-dialog">
          <DialogHeader><DialogTitle className="font-display">Add Fluid</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Name *</Label><Input data-testid="fluid-name" value={fluid.name} onChange={(ev) => setFluid({ ...fluid, name: ev.target.value })} placeholder="e.g. Engine oil" /></div>
              <div className="space-y-1.5"><Label>Type</Label><Input data-testid="fluid-type" value={fluid.type} onChange={(ev) => setFluid({ ...fluid, type: ev.target.value })} placeholder="e.g. 10W-30" /></div>
              <div className="space-y-1.5"><Label>Capacity</Label><Input data-testid="fluid-capacity" value={fluid.capacity} onChange={(ev) => setFluid({ ...fluid, capacity: ev.target.value })} placeholder="e.g. 2 qt" /></div>
              <div className="space-y-1.5"><Label>Last changed</Label><Input type="date" data-testid="fluid-date" value={fluid.last_changed} onChange={(ev) => setFluid({ ...fluid, last_changed: ev.target.value })} /></div>
              <div className="space-y-1.5"><Label>Cost</Label><Input type="number" data-testid="fluid-cost" value={fluid.cost} onChange={(ev) => setFluid({ ...fluid, cost: ev.target.value })} /></div>
            </div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setFluidOpen(false)}>Cancel</Button><Button data-testid="save-fluid-btn" onClick={saveFluid} className="bg-primary text-primary-foreground hover:bg-[#152e17]">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={() => setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete this item?</AlertDialogTitle><AlertDialogDescription>This can't be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction data-testid="confirm-delete" onClick={doConfirm} className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
